/**
 * Point d'entrée du dashboard personnalisable (route /).
 *
 * Architecture :
 * - GridStack gère la grille (position, taille, drag & drop)
 * - Chaque cellule contient un widget React rendu via portail (voir lib/grid-stack-render)
 * - Le mode personnalisation (DashboardEditContext) active déplacement / édition / suppression
 * - L'ajout ou la modification d'un widget passe par le parcours /widgets/* puis sessionStorage
 */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GridStackOptions, GridStackWidget } from "gridstack";
// CSS GridStack OBLIGATOIRE : positionne les items en absolu (transform selon
// gs-x/gs-y). Sans lui, tous les widgets s'empilent au même endroit (chevauchement).
import "gridstack/dist/gridstack.css";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ComponentMap,
  GridStackProvider,
  GridStackRender,
  GridStackRenderProvider,
  useGridStackContext,
} from "../../lib";

import { fetchDashboardConfig } from "../../../api/dashboard";
import { deserializeDashboard, serializeDashboard } from "./dashboardConfig";
import { DashboardCustomizeFab } from "./DashboardCustomizeFab";
import { DashboardEditProvider } from "./DashboardEditContext";
import { DashboardGridEditController } from "./DashboardGridEditController";
import { DashboardTutorial } from "./tutorial/DashboardTutorial";
import { buildDashboardGridOptions } from "./dashboardGridOptions";
import { DashboardSaveProvider, useDashboardSave } from "./DashboardSave";
import { DashboardSaveIndicator } from "./DashboardSaveIndicator";
import { setGridCellMetrics } from "./gridMetrics";
import {
  getWidgetCatalogEntry,
  getWidgetMinSize,
  WidgetTypeId,
} from "./widgetCatalog";
import { setWidgetCounts } from "./widgetCounts";
import {
  consumePendingWidget,
  consumePendingWidgetUpdate,
  parseWidgetContent,
} from "./widgetDraft";
import { AlarmsWidget } from "./widgets/AlarmsWidget";
import { RegulatorWidget } from "./widgets/RegulatorWidget";
import { ChartWidget } from "./widgets/ChartWidget";
import { ContactsWidget } from "./widgets/ContactsWidget";
import { NewsWidget } from "./widgets/NewsWidget";
import { TextWidget } from "./widgets/TextWidget";
import { WeatherWidget } from "./widgets/WeatherWidget";

/** Associe le nom stocké dans GridStack (ex. "RegulatorWidget") au composant React correspondant. */
const COMPONENT_MAP: ComponentMap = {
  RegulatorWidget,
  ChartWidget,
  WeatherWidget,
  ContactsWidget,
  AlarmsWidget,
  NewsWidget,
  TextWidget,
};

const widgetContent = (name: string, props: object = {}) => {
  return JSON.stringify({ name, props });
};

/**
 * Au retour sur le dashboard, consomme les widgets en attente (sessionStorage)
 * créés ou modifiés via le configurateur, puis les injecte dans la grille.
 */
function PendingWidgetHandler() {
  const { addWidget, updateWidget, gridStack } = useGridStackContext();
  const { requestSave } = useDashboardSave();

  useEffect(() => {
    // Attendre que GridStack soit initialisé avant de consommer le sessionStorage,
    // sinon addWidget met à jour la meta-map sans créer l'élément DOM.
    if (!gridStack) return;

    const pendingUpdate = consumePendingWidgetUpdate();
    if (pendingUpdate) {
      updateWidget(pendingUpdate.widgetId, {
        w: pendingUpdate.w,
        h: pendingUpdate.h,
        content: widgetContent(pendingUpdate.widgetType, pendingUpdate.props),
      });
      // Une édition de props (ex. ville météo) ne change pas forcément w/h et ne
      // déclenche donc aucun event GridStack → on persiste explicitement.
      requestSave();
      return;
    }

    const pending = consumePendingWidget();
    if (!pending) return;

    const id = `${pending.widgetType}-${Math.random().toString(36).substring(2, 9)}`;
    const { minW, minH } = getWidgetMinSize(pending.widgetType);
    addWidget({
      id,
      w: minW ? Math.max(pending.w, minW) : pending.w,
      h: minH ? Math.max(pending.h, minH) : pending.h,
      minW,
      minH,
      autoPosition: true,
      content: widgetContent(pending.widgetType, pending.props),
    } as GridStackWidget & { id: string });
    requestSave();
  }, [gridStack, addWidget, updateWidget, requestSave]);

  return null;
}

/**
 * Capte la largeur/hauteur de cellule réelles de la grille (responsive) et les
 * partage pour que l'aperçu du configurateur reflète la taille finale.
 */
function GridMetricsTracker() {
  const { gridStack } = useGridStackContext();

  useEffect(() => {
    if (!gridStack) return;
    const update = () => {
      setGridCellMetrics({
        cellWidth: gridStack.cellWidth(),
        cellHeight: gridStack.getCellHeight(true) ?? 50,
      });
    };
    update();

    const el = gridStack.el;
    if (!el) return;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [gridStack]);

  return null;
}

/**
 * Publie le décompte de widgets par type (miroir lu par la page catalogue pour
 * limiter certains types, ex. WeatherWidget max 3). Recalculé à chaque ajout /
 * suppression depuis la meta-map de la grille.
 */
function WidgetCountTracker() {
  const { gridStack, _rawWidgetMetaMap } = useGridStackContext();
  const metaMap = _rawWidgetMetaMap.value;

  useEffect(() => {
    const counts: Partial<Record<WidgetTypeId, number>> = {};
    metaMap.forEach((widget) => {
      const parsed = parseWidgetContent(widget.content);
      const type = parsed?.name as WidgetTypeId | undefined;
      if (type) counts[type] = (counts[type] ?? 0) + 1;
    });
    setWidgetCounts(counts);
  }, [metaMap, gridStack]);

  return null;
}

/**
 * État vide : quand la grille ne contient aucun widget, affiche un appel à l'action
 * centré (« ajoutez votre premier widget ») qui mène au catalogue (/widgets, même cible
 * que le FAB). Évite qu'un nouvel utilisateur se retrouve devant un écran vide sans repère.
 */
function EmptyDashboardHint() {
  const navigate = useNavigate();
  const { _rawWidgetMetaMap } = useGridStackContext();

  if (_rawWidgetMetaMap.value.size > 0) return null;

  // Overlay ancré en haut du conteneur `.relative` du dashboard : hors du flux, donc position
  // constante que la grille GridStack soit à 0 ou garde une hauteur résiduelle (après suppression
  // du dernier widget). `pointer-events-none` sur l'enveloppe pour ne rien bloquer autour.
  return (
    <div className="absolute left-0 right-0 top-0 flex justify-center px-6 pt-12 pointer-events-none">
      <button
        type="button"
        onClick={() => navigate("/widgets")}
        className="pointer-events-auto max-w-md w-full flex flex-col items-center justify-center gap-3 py-24 px-6 text-center
          border-2 border-dashed border-grey-light rounded-2xl text-grey-medium
          hover:border-secondary hover:text-secondary transition-colors"
      >
        <span className="flex items-center justify-center w-14 h-14 rounded-full bg-grey-light text-2xl">
          <FontAwesomeIcon icon="plus" />
        </span>
        <span className="font-oxaniumMedium text-lg">Votre dashboard est vide</span>
        <span className="text-sm">Cliquez ici pour ajouter votre premier widget</span>
      </button>
    </div>
  );
}

/** Props de démo par type de widget (Phase 1 : la donnée temps réel viendra en Phase 2). */
const getWidgetDefaultProps = (type: WidgetTypeId): Record<string, unknown> => {
  const entry = getWidgetCatalogEntry(type);
  return entry ? { ...entry.defaultProps } : {};
};

/** Assemble le provider GridStack, le rendu des widgets et les contrôles de personnalisation. */
function DashboardGridContent() {
  // Construit les options à partir de la config persistée du client ; null tant
  // que le chargement n'est pas terminé (GridStack fige initialOptions au montage).
  const [initialOptions, setInitialOptions] = useState<GridStackOptions | null>(
    null
  );
  // Référence de la config chargée : sert de base à la déduplication de l'autosave.
  const [baselineJson, setBaselineJson] = useState("");

  useEffect(() => {
    let active = true;

    // Pas de config → dashboard VIDE (aucun layout par défaut imposé).
    const empty = buildDashboardGridOptions([]);
    const emptyBaseline = JSON.stringify(serializeDashboard(empty));

    fetchDashboardConfig()
      .then((config) => {
        if (!active) return;
        if (config) {
          setInitialOptions(
            buildDashboardGridOptions(
              deserializeDashboard(config, getWidgetDefaultProps)
            )
          );
          setBaselineJson(JSON.stringify(config));
        } else {
          setInitialOptions(empty);
          setBaselineJson(emptyBaseline);
        }
      })
      .catch(() => {
        if (!active) return;
        setInitialOptions(empty);
        setBaselineJson(emptyBaseline);
      });

    return () => {
      active = false;
    };
  }, []);

  if (!initialOptions) {
    return (
      <div className="py-12 text-center text-grey-medium">
        Chargement du dashboard…
      </div>
    );
  }

  return (
    <GridStackProvider initialOptions={initialOptions}>
      <DashboardSaveProvider baselineJson={baselineJson}>
        <PendingWidgetHandler />
        <DashboardGridEditController />
        <GridMetricsTracker />
        <WidgetCountTracker />
        <GridStackRenderProvider>
          <GridStackRender componentMap={COMPONENT_MAP} />
        </GridStackRenderProvider>
        <EmptyDashboardHint />
        <DashboardCustomizeFab />
        <DashboardSaveIndicator />
      </DashboardSaveProvider>
    </GridStackProvider>
  );
}

/** Page dashboard : grille. Enveloppée dans DashboardEditProvider pour le mode édition. */
export const DashboardGrid = () => {
  return (
    <DashboardEditProvider>
      <div className="grid-stack-demo w-full min-w-0 min-h-screen overflow-x-hidden pb-24">
        <div className="relative p-4 w-full min-w-0">
          {/* Bouton de (re)lancement du tutoriel : flottant en haut à droite, sans
              réserver de hauteur (pas de barre vide en haut). NB : pas de transform
              sur ce conteneur, sinon les scènes de démo (position: fixed) seraient
              décalées. */}
          <div className="absolute top-4 right-4 z-10">
            <DashboardTutorial />
          </div>
          <div className="w-full min-w-0">
            <DashboardGridContent />
          </div>
          <p className="text-sm text-grey-medium mt-2 mb-4">
            Dernière mise à jour : il y a 2 min
          </p>
        </div>
      </div>
    </DashboardEditProvider>
  );
};
